import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography,
  IconButton, CircularProgress, Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  GppMaybe as RejectIcon,
  ErrorOutline as ErrorIcon,
  WarningAmber as WarningIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import axios from 'axios';

import { BRAND, FONT_STACK, GRADIENT_NAVY } from '../../brand';
import { claimStoreDetail } from '../BaseURL';

// ============================================================================
//  RejectReasonDialog — why THIS claim would be rejected
// ============================================================================
//
//  WHERE THE REJECTION COMES FROM, because it changes what this dialog can show.
//
//  It is COMPUTED, not stored. `claims` has a `status` ENUM that includes
//  'rejected' (migration 032) but that is the submission workflow — draft →
//  ready → submitted → approved/rejected → paid — and the badge is not driven by
//  it. There is no rejection_reason column on a claim and nobody has stamped a
//  claim with a reason, so there is no "rejected by X on date Y" to show.
//
//  The badge is driven by backend/claimRules.js, which every screen shares. The
//  claims LIST carries only rule CODES per row — `claimIssueFlags` evaluates the
//  SQL twins of the rules over the month in one pass, so a row knows THAT it
//  fails `ipd_on_opd_service` and nothing more. The specific field and value
//  live in the full evaluation, which needs the claim's own diagnoses and
//  procedures.
//
//  So this fetches /claims/store/detail on open and renders the `issues` array
//  claimIssues() attached to it. That array already carries, per rule:
//
//      title       the rule, in words
//      message     the sentence naming the actual codes on THIS claim
//      offending   [{ field, code }] — the field and the value that failed
//      suggested   what the claim's own rows say it should be
//
//  which is why this can say "specialty OPDC, service OPDC06A" rather than
//  "claim invalid". Nothing is re-derived here; a second opinion computed in the
//  browser is exactly how the badge and the explanation drift apart.
//
//  WARNINGS ARE SHOWN TOO, below the errors. The badge counts errors only — a
//  missing principal is true of nearly every imported claim and badging it would
//  put a pill on every row — but once you have opened a claim to ask why, the
//  rest of what is wrong with it is what you came for.

const TONE = {
  error: {
    fg: '#A62828', bg: '#FBE9E9', bd: '#F0C4C4',
    Icon: ErrorIcon, word: 'NHIA will reject this',
  },
  warning: {
    fg: '#8A5510', bg: '#FDF2E3', bd: '#F2DCB8',
    Icon: WarningIcon, word: 'Worth checking',
  },
};

//  One failed rule. The offending values are chips rather than prose so the
//  field and the value can be read without parsing the sentence — that is the
//  difference between a message you can act on and one you have to interpret.
const IssueCard = ({ issue }) => {
  const tone = TONE[issue.severity] || TONE.warning;
  const { Icon } = tone;
  return (
    <Box
      sx={{
        border: `1px solid ${tone.bd}`, borderRadius: '12px',
        background: tone.bg, overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, py: 1.75 }}>
        <Icon sx={{ fontSize: 20, color: tone.fg, flexShrink: 0, mt: '1px' }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: .5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: tone.fg, lineHeight: 1.3 }}>
              {issue.title || issue.code}
            </Typography>
            {/*  The severity in words. This dialog is opened from a red badge, so
                 a card that is merely amber inside it needs to say why. */}
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em',
              textTransform: 'uppercase', color: tone.fg, opacity: .75 }}>
              {tone.word}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: BRAND.ink }}>
            {issue.message}
          </Typography>

          {/*  THE FIELD AND THE VALUE. Named separately from the sentence so a
               claims officer can go straight to the field on the claim. */}
          {issue.offending?.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .75, mt: 1.25 }}>
              {issue.offending.map((o) => (
                <Box
                  key={`${o.field}-${o.code}`}
                  sx={{
                    display: 'inline-flex', alignItems: 'baseline', gap: .75,
                    px: 1, py: .35, borderRadius: '7px',
                    background: '#fff', border: `1px solid ${tone.bd}`,
                  }}
                >
                  <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '.05em',
                    textTransform: 'uppercase', color: BRAND.inkFaint }}>
                    {o.label || o.field}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: tone.fg,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {o.code}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/*  What the claim's OWN rows say it should be. Read-only here on
               purpose: repairing is the Repair button's job, and it previews
               before it writes. This dialog answers "why", not "fix it now". */}
          {issue.suggested?.service_code && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mt: 1.25, flexWrap: 'wrap' }}>
              <ArrowIcon sx={{ fontSize: 14, color: BRAND.greenDark }} />
              <Typography sx={{ fontSize: 12, color: BRAND.inkMuted }}>
                Its own {issue.suggested.from} gives
              </Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: BRAND.greenDark,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                {issue.suggested.service_code}
              </Typography>
              {issue.suggested.service_name && (
                <Typography sx={{ fontSize: 12, color: BRAND.inkMuted, minWidth: 0 }}>
                  — {issue.suggested.service_name}
                </Typography>
              )}
            </Box>
          )}

          {/*  A claim whose every G-DRG is an out-patient code cannot be settled
               from itself. Saying so is the difference between "press Repair"
               and "press Repair and wonder why nothing happened". */}
          {issue.severity === 'error' && issue.fixable === false && (
            <Typography sx={{ fontSize: 12, color: tone.fg, opacity: .9, mt: 1.25 }}>
              This one cannot be settled from the claim itself — open it and choose
              a service, or record the procedure that was actually done.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default function RejectReasonDialog({ open, claim, onClose, onOpenClaim, onResolved }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [issues, setIssues] = useState([]);

  const claimId = claim?.id;
  const source = claim?.source;

  useEffect(() => {
    if (!open || !claimId) return undefined;
    let alive = true;
    setLoading(true); setError(null); setIssues([]);

    axios.get(claimStoreDetail, { params: { id: claimId, source } })
      .then(({ data }) => {
        if (!alive) return;
        if (data?.success) {
          setIssues(data.issues || []);
          //  This request just computed the authoritative answer for this claim,
          //  so the row behind the dialog is told. Opening the reasons on a claim
          //  that has since been fixed therefore CLEARS its badge instead of
          //  advising a reload — which is what the officer was already doing by
          //  hand, on information the dialog was holding.
          if (onResolved) onResolved(data);
        } else setError(data?.message || 'Could not load this claim.');
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.message || e.message);
      })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, claimId, source]);

  //  Errors first. The thing that gets the claim rejected should not be third in
  //  a list of three.
  const ordered = [...issues].sort(
    (a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1));
  const errorCount = ordered.filter((i) => i.severity === 'error').length;

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      aria-labelledby="reject-reason-title"
      PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden', fontFamily: FONT_STACK, m: { xs: 1.5, sm: 4 } } }}
    >
      {/* ── header ── */}
      <Box sx={{ background: GRADIENT_NAVY, px: { xs: 2.5, sm: 3.5 }, py: 2.75, position: 'relative', overflow: 'hidden' }}>
        <Box aria-hidden sx={{ position: 'absolute', top: -60, right: -50, width: 190, height: 190, borderRadius: '50%', background: `radial-gradient(circle, ${BRAND.danger}40 0%, transparent 65%)` }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.22)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <RejectIcon sx={{ fontSize: 23, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '1.3px', textTransform: 'uppercase', color: 'rgba(255,255,255,.55)' }}>
              Claim {claim?.folder_no || claimId || ''}
            </Typography>
            <Typography id="reject-reason-title" noWrap sx={{ fontSize: 18.5, fontWeight: 800, color: '#fff', letterSpacing: '-.3px', lineHeight: 1.25 }}>
              {claim?.name || 'Why this would be rejected'}
            </Typography>
          </Box>
          <IconButton
            size="small" onClick={onClose} aria-label="Close"
            sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', flexShrink: 0, '&:hover': { bgcolor: 'rgba(255,255,255,.26)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3, background: '#fff' }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 5 }}>
            <CircularProgress size={20} sx={{ color: BRAND.navy }} />
            <Typography sx={{ fontSize: 13, color: BRAND.inkMuted }}>Checking this claim…</Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>
        )}

        {/*  The badge is drawn from a SQL pass over the month; this dialog
             re-asks the full rules on the one claim. If the claim was corrected
             in between, it really is clean now — and `onResolved` above has
             already told the row, so the badge is gone by the time this is read.
             It does NOT tell the officer to reload: that was asking them to do
             by hand what the request had just made possible. */}
        {!loading && !error && ordered.length === 0 && (
          <Alert severity="success" sx={{ borderRadius: '12px' }}>
            Nothing is wrong with this claim — every rule passes. The flag on its
            row has been cleared.
          </Alert>
        )}

        {!loading && !error && ordered.length > 0 && (
          <>
            <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, mb: 2, lineHeight: 1.6 }}>
              {errorCount > 0
                ? `${errorCount} rule${errorCount === 1 ? '' : 's'} would have NHIA reject this claim.`
                : 'Nothing here blocks submission, but these are worth a look.'}
              {' '}Checked against the claim’s own diagnoses and procedures.
            </Typography>

            {/*  CSS Grid, not <Grid item xs md> — MUI 7 ignores that API outright
                 and every child would render full-width with no warning. */}
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {ordered.map((issue, i) => (
                <IssueCard key={`${issue.code}-${i}`} issue={issue} />
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, borderTop: `1px solid ${BRAND.line}`, background: '#fff', gap: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, color: BRAND.inkMuted }}>
          Close
        </Button>
        <Box sx={{ flex: 1 }} />
        {onOpenClaim && (
          <Button
            onClick={() => { onClose(); onOpenClaim(claim); }}
            variant="contained"
            sx={{
              px: 3.5, py: 1.05, borderRadius: '11px', textTransform: 'none', fontWeight: 700,
              background: `linear-gradient(135deg, ${BRAND.greenLight} 0%, ${BRAND.green} 55%, ${BRAND.greenDark} 100%)`,
              boxShadow: `0 4px 14px ${BRAND.green}42`,
              '&:hover': { filter: 'brightness(1.06)' },
            }}
          >
            Open the claim
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
